import { Injectable } from '@angular/core';
import { WorkItemActionType } from 'src/app/models/client-microservice/client-configuration';
import {
  DatePattern,
  DatePatternDaysOfWeek
} from 'src/app/models/date-pattern';
import {
  DeviationType,
  ManipulateGeneratedItems,
  RepeatTypes
} from 'src/app/models/enums';
import {
  DatePatternBaseDto,
  DatePatternType,
  DayType,
  HolidayTaskRule
} from 'src/app/models/process/date-pattern-base-dto';
import { DatePatternMonthlyDto } from 'src/app/models/process/date-pattern-monthly-dto';
import { DatePatternWeeklyDto } from 'src/app/models/process/date-pattern-weekly-dto';
import {
  DatePatternYearlyDto,
  DatePatternYearlyMonthDto,
  Months
} from 'src/app/models/process/date-pattern-yearly-dto';

@Injectable({
  providedIn: 'root'
})
export class MonoToDtoDatePatternService {
  mono2dto(mono: DatePattern): DatePatternBaseDto {
    let dto = new DatePatternBaseDto();

    dto.isNew = mono.isNew;
    dto.externalId = mono.id;
    dto.name = mono.name;
    dto.description = mono.summary;
    dto.interval = mono.repeat;
    dto.period = mono.periodRule;
    dto.numberOfDaysToStartBeforeDeadline = mono.startTask;
    dto.daysToStartBeforeDeadlineType =
      mono.startType == DeviationType.WorkingDays
        ? DayType.WorkingDays
        : DayType.Days;
    dto.holidayRuleCountryCodes = mono.countryCodes;
    dto.order = mono.order;
    dto.intervalFromDate = mono.intervalFromDate
      ? mono.intervalFromDate.toISOString()
      : null;
    dto.holidayRuleType = this.convertMono2DtoHolidayTaskRule(
      mono.holidayTaskRule
    );
    dto.customHolidays = mono.customHolidays;
    dto.saveOption = this.convertMono2DtoSaveOption(mono.saveOption);

    if (mono.repeatType == RepeatTypes.Daily) {
      dto.type = DatePatternType.Daily;
    } else if (mono.repeatType == RepeatTypes.Info) {
      dto.type = DatePatternType.Info;
    } else if (mono.repeatType == RepeatTypes.Monthly) {
      dto.type = DatePatternType.Monthly;
      const dtoMonthly: DatePatternMonthlyDto = JSON.parse(JSON.stringify(dto));
      dtoMonthly.days = mono.monthlyPattern.monthlyDays;
      dtoMonthly.workingDays = mono.monthlyPattern.monthlyWorkingDays;
      dtoMonthly.deviation = mono.monthlyPattern.deviation;
      dtoMonthly.deviationType =
        mono.monthlyPattern.deviationType == DeviationType.WorkingDays
          ? DayType.WorkingDays
          : DayType.Days;
      dto = dtoMonthly;
    } else if (mono.repeatType == RepeatTypes.Weekly) {
      dto.type = DatePatternType.Weekly;
      const dtoWeekly: DatePatternWeeklyDto = JSON.parse(JSON.stringify(dto));
      dtoWeekly.monday = mono.daysOfWeek?.includes(
        DatePatternDaysOfWeek.Monday
      );
      dtoWeekly.tuesday = mono.daysOfWeek?.includes(
        DatePatternDaysOfWeek.Tuesday
      );
      dtoWeekly.wednesday = mono.daysOfWeek?.includes(
        DatePatternDaysOfWeek.Wednesday
      );
      dtoWeekly.thursday = mono.daysOfWeek?.includes(
        DatePatternDaysOfWeek.Thursday
      );
      dtoWeekly.friday = mono.daysOfWeek?.includes(
        DatePatternDaysOfWeek.Friday
      );
      dtoWeekly.saturday = mono.daysOfWeek?.includes(
        DatePatternDaysOfWeek.Saturday
      );
      dtoWeekly.sunday = mono.daysOfWeek?.includes(
        DatePatternDaysOfWeek.Sunday
      );
      dto = dtoWeekly;
    } else if (mono.repeatType == RepeatTypes.Yearly) {
      dto.type = DatePatternType.Yearly;
      const dtoYearly: DatePatternYearlyDto = JSON.parse(JSON.stringify(dto));
      dtoYearly.useCustomStartTask = mono.isStartTaskCustom;
      dtoYearly.months = mono.yearlyPatterns?.map(monoYearlyPattern => {
        const yearlyPattern = new DatePatternYearlyMonthDto();
        yearlyPattern.workingDays = monoYearlyPattern.workingDays;
        yearlyPattern.days = monoYearlyPattern.days;
        yearlyPattern.customDaysToStartBeforeDeadlineType =
          monoYearlyPattern.startType == DeviationType.WorkingDays
            ? DayType.WorkingDays
            : DayType.Days;
        yearlyPattern.customNumberOfDaysToStartBeforeDeadline =
          monoYearlyPattern.startTask;
        yearlyPattern.month = this.convertMono2DtoMonth(
          monoYearlyPattern.month
        );
        yearlyPattern.deviation = monoYearlyPattern.deviation;
        yearlyPattern.deviationType =
          monoYearlyPattern.deviationType == DeviationType.WorkingDays
            ? DayType.WorkingDays
            : DayType.Days;
        return yearlyPattern;
      });
      dto = dtoYearly;
    }

    return dto;
  }

  private convertMono2DtoMonth(month: number): Months {
    switch (month) {
      case 1:
        return Months.January;
      case 2:
        return Months.February;
      case 3:
        return Months.March;
      case 4:
        return Months.April;
      case 5:
        return Months.May;
      case 6:
        return Months.June;
      case 7:
        return Months.July;
      case 8:
        return Months.August;
      case 9:
        return Months.September;
      case 10:
        return Months.October;
      case 11:
        return Months.November;
      case 12:
        return Months.December;
    }
  }

  private convertMono2DtoHolidayTaskRule(
    holidayTaskRule: number
  ): HolidayTaskRule {
    if (holidayTaskRule == 1) {
      return HolidayTaskRule.Previous;
    } else if (holidayTaskRule == 2) {
      return HolidayTaskRule.Next;
    } else {
      return HolidayTaskRule.None;
    }
  }

  private convertMono2DtoSaveOption(
    saveOption: ManipulateGeneratedItems
  ): WorkItemActionType {
    if (saveOption == ManipulateGeneratedItems.Remove) {
      return WorkItemActionType.Remove;
    } else if (saveOption == ManipulateGeneratedItems.Update) {
      return WorkItemActionType.Update;
    } else {
      return WorkItemActionType.Keep;
    }
  }
}
